import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../lib/apiClient';
import type { StationScopeRequest } from './stationScopeRequest';

// CTV-743: is this station's payout destination connected?
//
// One hook so the sidebar indicator and the prompt banner cannot disagree,
// and so the predicate lives in exactly one place on the client.
//
// CTV-759: reads the authenticated POST /stations/payout-status instead of
// the @Public() GET /stations/:slug, which used to carry the whole Stripe
// block to unauthenticated visitors. Because that route is scoped, the
// aggregated roll-up is ONE request covering every station rather than N.

export interface PayoutDestination {
  accountId: string | null;
  accountMode: 'recipient' | 'oauth' | null;
  payoutsEnabled: boolean | null;
}

export interface PayoutStatusRow extends PayoutDestination {
  slug: string | null;
  displayName: string;
  detailsSubmitted: boolean | null;
  onboardingStartedAt: string | null;
  onboardingCompletedAt: string | null;
}

/**
 * The settlement cron's own gate: `stripe_account_id IS NOT NULL AND
 * stripe_payouts_enabled AND stripe_account_mode IS NOT NULL`. Anything less
 * and the engine skips the station, so anything less is "not set up".
 *
 * accountMode is the one that catches real stations. NULL does not mean
 * "unset", it means "not connected under the current model": the CTV-518
 * merchant-of-record cutover reset every legacy Express connection, so a
 * station can carry an account id AND payoutsEnabled and still never be paid.
 *
 * THE definition for the whole client (CTV-743). The payments page used to
 * carry its own ("account id AND (oauth OR details submitted)"), which could
 * show a publisher a green connected state while the engine would not pay
 * them. One predicate or the surfaces drift again. CTV-759 applies it per
 * row rather than forking it for the aggregated case.
 */
export function isPayoutDestinationReady(
  destination: PayoutDestination | null | undefined,
): boolean {
  if (!destination) return false;
  return (
    !!destination.accountId &&
    destination.payoutsEnabled === true &&
    destination.accountMode != null
  );
}

export function usePayoutSetupStatus(
  scope: StationScopeRequest | null,
  enabled: boolean,
) {
  // `null` while unknown, so no caller renders an alarm before the answer
  // arrives. A station that IS connected must never flash a red dot.
  const [rows, setRows] = useState<PayoutStatusRow[] | null>(null);
  // Serialized so the effect's dependency is stable across re-renders that
  // rebuild an equal scope object.
  const scopeKey = scope ? JSON.stringify(scope) : null;

  const refresh = useCallback(async () => {
    if (!enabled || !scopeKey) {
      setRows(null);
      return;
    }
    try {
      const { rows: fetched } = await apiClient.post<{ rows: PayoutStatusRow[] }>(
        '/stations/payout-status',
        JSON.parse(scopeKey) as StationScopeRequest,
      );
      setRows(fetched ?? []);
    } catch {
      // A failed lookup is not evidence of a missing payout destination, and
      // crying wolf on a network blip would train people to ignore the dot.
      setRows(null);
    }
  }, [scopeKey, enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Stations known NOT to be connected. Empty while the answer is unknown.
  const unready = (rows ?? []).filter((r) => !isPayoutDestinationReady(r));

  return {
    rows,
    unready,
    /** True when at least one station in scope is known to need setup. */
    needsSetup: unready.length > 0,
    refresh,
  };
}
